import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { isNative } from "@/lib/appConfig";
import { hapticTap } from "@/lib/haptics";

// iOS-only swipe-back gesture: touch must START within 24px of the left edge,
// travel horizontally at least 60px, and be predominantly horizontal (dx > dy).
// Guards against firing when a Radix overlay or the mobile drawer is open.
// On web and Android this component is a no-op to avoid interfering with UI.
export default function SwipeBack() {
    const navigate = useNavigate();
    const location = useLocation();
    const navigationType = useNavigationType();
    const startX = useRef(null);
    const startY = useRef(null);
    const fired = useRef(false);
    // window.history.length doesn't reliably track React Router navigation depth
    // inside Capacitor's WebView, which made the old length<=1 guard unreliable
    // (sometimes stuck at 1 forever, silently killing the gesture). Track depth
    // ourselves from actual route transitions instead.
    const depthRef = useRef(0);

    useEffect(() => {
        if (navigationType === "PUSH") depthRef.current += 1;
        else if (navigationType === "POP") depthRef.current = Math.max(0, depthRef.current - 1);
    }, [navigationType, location]);

    useEffect(() => {
        if (!isNative()) return;
        const platform = window.Capacitor?.getPlatform?.();
        if (platform !== "ios") return;

        function hasOpenOverlay() {
            if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) return true;
            if (document.querySelector('[data-testid="mobile-drawer"][data-open="true"]')) return true;
            return false;
        }

        function onTouchStart(e) {
            fired.current = false;
            const touch = e.touches[0];
            startX.current = touch.clientX;
            startY.current = touch.clientY;
        }

        function onTouchMove(e) {
            if (fired.current) return;
            if (startX.current === null || startY.current === null) return;
            if (startX.current > 24) return;

            const touch = e.touches[0];
            const dx = touch.clientX - startX.current;
            const dy = Math.abs(touch.clientY - startY.current);

            if (dx < 60) return;
            if (dy > dx * 0.6) return;

            if (hasOpenOverlay()) return;
            if (depthRef.current <= 0) return;

            fired.current = true;
            hapticTap();
            navigate(-1);
        }

        function onTouchEnd() {
            startX.current = null;
            startY.current = null;
            fired.current = false;
        }

        document.addEventListener("touchstart", onTouchStart, { passive: true });
        document.addEventListener("touchmove", onTouchMove, { passive: true });
        document.addEventListener("touchend", onTouchEnd, { passive: true });

        return () => {
            document.removeEventListener("touchstart", onTouchStart);
            document.removeEventListener("touchmove", onTouchMove);
            document.removeEventListener("touchend", onTouchEnd);
        };
    }, [navigate]);

    return null;
}
